import type { FeatureFlagsType } from 'const/featureFlags'
import type { Modals } from 'store/atoms/typings'
import type { LanguageObject, TranslationKey } from 'strings/lang/typings'
import type { IVALaunchPointName } from 'typings/custom'
import type { FeatureContext } from 'helpers/featureContext'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import {
  activityModalContainerId,
  balanceTransferOffersModalContainerId,
  cardStatusTrackerModalContainerId,
  changePaymentDueDateModalContainerId,
  creditBalanceRefundModalContainerId,
  creditLimitIncreaseContainerId,
  digitalCollectionsContainerId,
  makeAPaymentModalContainerId,
  manageCardHoldersModalContainerId,
  paymentSecurityModalContainerId,
  replaceYourCardModalContainerId,
  reportLostOrStolenCardModalContainerId,
  requestPastStatementsModalContainerId,
  updateIncomeModalContainerId
} from 'const/modalContainerId'
import routes from 'const/routes'

export type SearchCategory = 'Pages' | 'Account Actions'

export type SearchItemId = keyof LanguageObject['searchTags']

export type EligibilityRule = (ctx: FeatureContext) => boolean

/**
 * `featureFlag` off + `ivaFallback` set → route to IVA at click time.
 * `featureFlag` off + no `ivaFallback`  → item is hidden from results.
 */
export type SearchAction =
  | {
      type: 'route'
      to: string
      parentRoute?: typeof routes.ACCOUNT_SERVICES
    }
  | { type: 'modal'; modal: Modals }
  | {
      type: 'modal'
      modal: Modals
      getTriggerId: (accountId: string) => string
    }
  | {
      type: 'modal'
      modal: Modals
      // HOME: the modal belongs to the dashboard account card, not the current page.
      parentRoute: typeof routes.ACCOUNT_SERVICES | typeof routes.HOME
      getTriggerId: (accountId: string) => string
    }
  | {
      type: 'iva'
      pointOfNeed: IVALaunchPointName
      parentRoute?: typeof routes.ACCOUNT_SERVICES
    }

export interface SearchItem {
  id: SearchItemId
  labelKey: TranslationKey
  action: SearchAction
  category: SearchCategory
  isAccountScoped: boolean
  isEligible: EligibilityRule | null
  featureFlag?: keyof FeatureFlagsType
  ivaFallback?: IVALaunchPointName
  /** Hidden when the session came from an LWS (partner) redirect. */
  isHiddenForLws?: boolean
  /** Per-account override of `action`; undefined keeps the default. */
  getAction?: (ctx: FeatureContext) => SearchAction | undefined
  analyticsReason: string
}

const PAGES: SearchCategory = 'Pages'
const ACCOUNT_ACTIONS: SearchCategory = 'Account Actions'

const ACCOUNT_DETAILS_ID: SearchItemId = 'account-details'
const ACCOUNT_SERVICES_ID: SearchItemId = 'account-services'
const ACTIVITY_ID: SearchItemId = 'activity'
const CREDIT_BALANCE_REFUND_ID: SearchItemId = 'credit-balance-refund'
const DIGITAL_COLLECTIONS_ID: SearchItemId = 'digital-collections'
const MAKE_A_PAYMENT_ID: SearchItemId = 'make-a-payment'
const PROMOTIONS_ID: SearchItemId = 'promotions'
const REWARDS_ID: SearchItemId = 'rewards'
const STATEMENTS_ID: SearchItemId = 'statements'

const isAccountServicesEligible: EligibilityRule = (ctx) => {
  if (ctx.isSecuredInstallment || ctx.isSetpayOrPaylater) return false
  if (ctx.clientId === 'cb' && !ctx.hasAccountServicingEnabled) return false
  if ((ctx.isAccountOpen || ctx.isClosedWithBalance) && ctx.inCapOrSettlement) {
    return false
  }
  return !(ctx.isClosedWithBalance && ctx.currentBalance <= 0)
}

const isCardFooterVisible = (ctx: FeatureContext) => !ctx.showsRegisterButton

const isPaymentSlotVisible = (ctx: FeatureContext) =>
  !(isFeatureFlagEnabled('isSettlementPlanEnabled') && ctx.isSettlementLoan)

/**
 * Conditions account-card-mfe shouldShowPaymentButton applies that
 * getPaymentButtonType does not. Layered on top rather than folded into the
 * shared helper, which also drives the page header button.
 */
const isMakeAPaymentEligible: EligibilityRule = (ctx) => {
  if (!ctx.shouldShowMakeAPayment) return false

  const isCollectionsEligible = isFeatureFlagEnabled(
    'isDigitalCollectionsExperienceEnabled'
  )
    ? !ctx.isDelinquent || ctx.inCapOrSettlement
    : (!ctx.inCapOrSettlement && !ctx.isDelinquent) || !ctx.isRegistered

  if (!ctx.isSetpayOrPaylater && !isCollectionsEligible) return false
  if (ctx.isSecuredInstallment && (ctx.isDormant || ctx.currentBalance <= 0)) {
    return false
  }
  return !(
    (ctx.isClosed || ctx.isClosedWithBalance) &&
    (ctx.isPreDelivery || ctx.currentBalance <= 0)
  )
}

/**
 * Mirrors the account card collections button: the payment slot renders one
 * button only, and credit balance refund then make a payment take precedence.
 */
const isDigitalCollectionsEligible: EligibilityRule = (ctx) => {
  if (!ctx.shouldShowCollectionPayment) return false
  if (ctx.shouldShowCreditBalanceRefund || isMakeAPaymentEligible(ctx)) {
    return false
  }
  if (ctx.isSetpayOrPaylater || ctx.isSecuredInstallment) return false
  return ctx.isAccountOpen || ctx.isClosedWithBalance
}

const hiddenByRegisterButton = new Set<SearchItemId>([
  ACCOUNT_DETAILS_ID,
  ACCOUNT_SERVICES_ID,
  ACTIVITY_ID,
  PROMOTIONS_ID,
  REWARDS_ID,
  STATEMENTS_ID
])

const hiddenBySettlementPlan = new Set<SearchItemId>([
  CREDIT_BALANCE_REFUND_ID,
  DIGITAL_COLLECTIONS_ID,
  MAKE_A_PAYMENT_ID
])

export const isSearchItemEligible = (
  item: SearchItem,
  ctx: FeatureContext
): boolean => {
  const requiresAccountServices =
    'parentRoute' in item.action &&
    item.action.parentRoute === routes.ACCOUNT_SERVICES

  // The overlay replaces the whole card footer, so only card-reached items are hidden.
  const isReachedFromCard =
    hiddenByRegisterButton.has(item.id) ||
    hiddenBySettlementPlan.has(item.id) ||
    requiresAccountServices

  if (isReachedFromCard && ctx.isCardOverlaid) return false

  if (
    (hiddenByRegisterButton.has(item.id) || requiresAccountServices) &&
    !isCardFooterVisible(ctx)
  ) {
    return false
  }
  if (hiddenBySettlementPlan.has(item.id) && !isPaymentSlotVisible(ctx)) {
    return false
  }
  if (requiresAccountServices && !isAccountServicesEligible(ctx)) return false

  return !item.isEligible || item.isEligible(ctx)
}

const searchItems: SearchItem[] = [
  {
    id: ACCOUNT_SERVICES_ID,
    labelKey: { labels: 'accountsServices' },
    action: { type: 'route', to: routes.ACCOUNT_SERVICES },
    category: PAGES,
    isAccountScoped: true,
    analyticsReason: ACCOUNT_SERVICES_ID,
    isEligible: isAccountServicesEligible
  },
  {
    id: 'alerts',
    labelKey: { button: 'alerts' },
    action: { type: 'route', to: routes.ALERTS },
    category: PAGES,
    isAccountScoped: true,
    isEligible: null,
    analyticsReason: 'alerts'
  },
  {
    id: 'credit-view',
    labelKey: { button: 'creditViewDashboard' },
    action: {
      type: 'route',
      to: routes.CREDIT_VIEW,
      parentRoute: routes.ACCOUNT_SERVICES
    },
    category: PAGES,
    isAccountScoped: true,
    isEligible: (ctx) => ctx.hasAccessToCreditView,
    analyticsReason: 'credit-view'
  },
  {
    id: 'disputes-center',
    labelKey: { button: 'manageYourDisputes' },
    action: {
      type: 'route',
      to: routes.DISPUTES,
      parentRoute: routes.ACCOUNT_SERVICES
    },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: true,
    isEligible: null,
    analyticsReason: 'manage your disputes'
  },
  {
    id: 'document-center',
    labelKey: { button: 'documentCenter' },
    action: { type: 'route', to: routes.DOCUMENT_CENTER },
    category: PAGES,
    isAccountScoped: true,
    featureFlag: 'isDocumentCenterEnabled',
    isEligible: (ctx) => !ctx.isSetpayOrPaylater,
    analyticsReason: 'document center'
  },
  {
    id: 'marketing-privacy',
    labelKey: { button: 'marketingSetting' },
    action: { type: 'route', to: routes.MARKETING_PRIVACY },
    category: PAGES,
    isAccountScoped: false,
    isEligible: null,
    analyticsReason: 'marketing & privacy settings'
  },
  {
    id: 'profile',
    labelKey: { button: 'profileManagement' },
    action: { type: 'route', to: routes.PROFILE },
    category: PAGES,
    isAccountScoped: true,
    isEligible: null,
    analyticsReason: 'Profile management'
  },
  {
    id: PROMOTIONS_ID,
    labelKey: { labels: 'promotionsMenu' },
    action: { type: 'route', to: routes.PROMOTIONS },
    category: PAGES,
    isAccountScoped: true,
    isEligible: (ctx) =>
      ctx.hasAccessToPromotions &&
      !ctx.isSecuredInstallment &&
      !ctx.isPreDelivery,
    analyticsReason: 'promotions'
  },
  {
    id: REWARDS_ID,
    labelKey: { button: 'rewards' },
    action: { type: 'route', to: routes.REWARDS },
    category: PAGES,
    isAccountScoped: true,
    analyticsReason: 'rewards',
    isEligible: (ctx) => {
      if (!ctx.hasAccessToRewards || !ctx.hasAccessToRewardsMfe) return false
      if (
        ctx.clientId === 'sams' &&
        !isFeatureFlagEnabled('isSamsRewardsMfeEnabled')
      ) {
        return false
      }
      return !ctx.isSecuredInstallment
    }
  },
  {
    id: STATEMENTS_ID,
    labelKey: { labels: 'statements' },
    action: { type: 'route', to: routes.STATEMENTS },
    category: PAGES,
    isAccountScoped: true,
    analyticsReason: 'statements',
    isEligible: (ctx) =>
      ctx.hasAccessToStatements &&
      !ctx.isSetpayOrPaylater &&
      !ctx.isSecuredInstallment
  },
  {
    id: 'credit-score',
    labelKey: { button: 'yourCreditScore' },
    action: { type: 'route', to: routes.CREDIT_SCORE },
    category: PAGES,
    isAccountScoped: false,
    isEligible: null,
    analyticsReason: 'credit score'
  },
  {
    id: ACCOUNT_DETAILS_ID,
    labelKey: { button: 'accountDetails' },
    action: { type: 'route', to: routes.ACCOUNT_DETAILS },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: true,
    analyticsReason: 'account details',
    isEligible: (ctx) =>
      !(isFeatureFlagEnabled('isSettlementPlanEnabled') && ctx.isSettlementLoan)
  },
  {
    id: ACTIVITY_ID,
    labelKey: { labels: 'activityMenu' },
    action: { type: 'route', to: routes.ACTIVITY },
    category: PAGES,
    isAccountScoped: true,
    analyticsReason: 'activity',
    isEligible: (ctx) =>
      !ctx.isSecuredInstallment ||
      isFeatureFlagEnabled('showActivityForSecuredInstallment'),
    // Loans only get the Activity drawer from the dashboard card, never the full page.
    getAction: (ctx) =>
      ctx.isSetpayOrPaylater || ctx.isSecuredInstallment
        ? {
            type: 'modal',
            modal: 'Activity',
            parentRoute: routes.HOME,
            getTriggerId: activityModalContainerId
          }
        : undefined
  },
  {
    id: 'activate-card',
    labelKey: { button: 'activateACard' },
    action: {
      type: 'route',
      to: routes.CARD_ACTIVATION,
      parentRoute: routes.ACCOUNT_SERVICES
    },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: true,
    featureFlag: 'isPostLoginActivationEnabled',
    ivaFallback: 'AC',
    isEligible: (ctx) => ctx.isRevolving,
    analyticsReason: 'activate a card'
  },
  {
    id: 'add-account',
    labelKey: { button: 'addAccount' },
    action: {
      type: 'modal',
      modal: 'AddAccount',
      parentRoute: routes.HOME,
      // Same trigger id as the dashboard AddAccountButton.
      getTriggerId: () => 'add-account-container-id'
    },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: false,
    isEligible: null,
    // Mirrors the dashboard and avatar-menu Add Account entry points.
    isHiddenForLws: true,
    analyticsReason: 'add account'
  },
  {
    id: 'balance-transfer',
    labelKey: { button: 'balanceTransferOffers' },
    action: {
      type: 'modal',
      modal: 'BalanceTransferOffers',
      parentRoute: routes.ACCOUNT_SERVICES,
      getTriggerId: balanceTransferOffersModalContainerId
    },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: true,
    featureFlag: 'isBalanceTransfersEnabled',
    isEligible: (ctx) => ctx.hasAccessToBalanceTransfers,
    analyticsReason: 'balance transfer'
  },
  {
    id: 'card-status-tracker',
    labelKey: { button: 'trackYourCard' },
    action: {
      type: 'modal',
      modal: 'CardStatusTracker',
      parentRoute: routes.ACCOUNT_SERVICES,
      getTriggerId: cardStatusTrackerModalContainerId
    },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: true,
    featureFlag: 'isCardStatusTrackerEnabled',
    isEligible: null,
    analyticsReason: 'track your card'
  },
  {
    id: 'change-payment-due-date',
    labelKey: { button: 'changePaymentDueDate' },
    action: {
      type: 'modal',
      modal: 'ChangePaymentDueDate',
      parentRoute: routes.ACCOUNT_SERVICES,
      getTriggerId: changePaymentDueDateModalContainerId
    },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: true,
    isEligible: (ctx) => ctx.hasAccessToChangeDueDate,
    analyticsReason: 'change payment due date'
  },
  {
    id: 'create-change-pin',
    labelKey: { button: 'createChangePIN' },
    action: {
      type: 'route',
      to: routes.CHANGE_PIN,
      parentRoute: routes.ACCOUNT_SERVICES
    },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: true,
    featureFlag: 'isCreateOrChangePinEnabled',
    ivaFallback: 'CCP',
    isEligible: (ctx) =>
      ctx.isRevolving &&
      !ctx.isDelinquent &&
      (!isFeatureFlagEnabled('isCreateOrChangePinEnabled') ||
        ctx.hasCashAdvancePinEnabled),
    analyticsReason: 'create/change PIN'
  },
  {
    id: 'credit-limit-increase',
    labelKey: { button: 'creditLimitIncrease' },
    action: {
      type: 'modal',
      modal: 'CreditLimitIncrease',
      parentRoute: routes.ACCOUNT_SERVICES,
      getTriggerId: creditLimitIncreaseContainerId
    },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: true,
    isEligible: (ctx) =>
      ctx.isRevolving &&
      !ctx.isDelinquent &&
      ctx.hasAccessToCreditLimitIncrease &&
      ctx.isCreditLineIncreaseEligible,
    analyticsReason: 'credit limit increase'
  },
  {
    id: 'freeze-card',
    labelKey: { button: 'freezeCard' },
    action: {
      type: 'iva',
      pointOfNeed: 'FC',
      parentRoute: routes.ACCOUNT_SERVICES
    },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: true,
    isEligible: (ctx) => ctx.isRevolving && !ctx.isDelinquent,
    analyticsReason: 'freeze card'
  },
  {
    id: 'replace-your-card',
    labelKey: { button: 'replaceYourCard' },
    action: {
      type: 'modal',
      modal: 'ReplaceYourCard',
      parentRoute: routes.ACCOUNT_SERVICES,
      getTriggerId: replaceYourCardModalContainerId
    },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: true,
    featureFlag: 'isReplaceYourCardEnabled',
    ivaFallback: 'RC',
    isEligible: (ctx) => ctx.isRevolving || ctx.isDelinquent,
    analyticsReason: 'replace your card'
  },
  {
    id: 'report-lost-stolen',
    labelKey: { button: 'reportLostStolenCard' },
    action: {
      type: 'modal',
      modal: 'ReportLostOrStolenCard',
      parentRoute: routes.ACCOUNT_SERVICES,
      getTriggerId: reportLostOrStolenCardModalContainerId
    },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: true,
    featureFlag: 'isReportLostOrStolenCardEnabled',
    ivaFallback: 'RLS',
    isEligible: (ctx) => ctx.isRevolving || ctx.isDelinquent,
    analyticsReason: 'report lost or stolen card'
  },
  {
    id: 'request-past-statements',
    labelKey: { button: 'requestPastStatements' },
    action: {
      type: 'modal',
      modal: 'RequestPastStatements',
      parentRoute: routes.ACCOUNT_SERVICES,
      getTriggerId: requestPastStatementsModalContainerId
    },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: true,
    featureFlag: 'isRequestPastStatementEnabled',
    isEligible: () => isFeatureFlagEnabled('isDocumentCenterEnabled'),
    analyticsReason: 'request past statements'
  },
  {
    id: 'manage-cardholders',
    labelKey: { button: 'manageCardholders' },
    action: {
      type: 'modal',
      modal: 'ManageCardholders',
      parentRoute: routes.ACCOUNT_SERVICES,
      getTriggerId: manageCardHoldersModalContainerId
    },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: true,
    analyticsReason: 'manage cardholders',
    isEligible: (ctx) => {
      if (!ctx.isServiceable || ctx.isClosed || ctx.isClosedWithBalance) {
        return false
      }
      return ctx.hasAccessToManageAuthUser || ctx.clientId === 'sams'
    }
  },
  {
    id: MAKE_A_PAYMENT_ID,
    labelKey: { button: 'makeAPayment' },
    action: {
      type: 'modal',
      modal: 'MAP',
      parentRoute: routes.HOME,
      getTriggerId: makeAPaymentModalContainerId
    },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: true,
    isEligible: isMakeAPaymentEligible,
    analyticsReason: 'make a payment'
  },
  {
    id: 'payment-security',
    labelKey: { button: 'paymentSecurity' },
    action: {
      type: 'modal',
      modal: 'PaymentSecurity',
      parentRoute: routes.ACCOUNT_SERVICES,
      getTriggerId: paymentSecurityModalContainerId
    },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: true,
    featureFlag: 'isPaymentSecurityEnabled',
    isEligible: (ctx) => ctx.hasAccessToPaymentSecurity,
    analyticsReason: 'payment security'
  },
  {
    id: DIGITAL_COLLECTIONS_ID,
    labelKey: { collectionsModal: 'modalTitle' },
    action: {
      type: 'modal',
      modal: 'DigitalCollections',
      parentRoute: routes.HOME,
      getTriggerId: digitalCollectionsContainerId
    },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: true,
    featureFlag: 'isDigitalCollectionsExperienceEnabled',
    isEligible: isDigitalCollectionsEligible,
    analyticsReason: 'digital collections'
  },
  {
    id: CREDIT_BALANCE_REFUND_ID,
    labelKey: { button: 'creditBalanceRefund' },
    action: {
      type: 'modal',
      modal: 'CreditBalanceRefund',
      parentRoute: routes.HOME,
      getTriggerId: creditBalanceRefundModalContainerId
    },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: true,
    isEligible: (ctx) => ctx.shouldShowCreditBalanceRefund && ctx.isCardProduct,
    analyticsReason: 'credit balance refund'
  },
  {
    id: 'update-income',
    labelKey: { button: 'updateIncome' },
    action: {
      type: 'modal',
      modal: 'UpdateIncome',
      parentRoute: routes.HOME,
      getTriggerId: updateIncomeModalContainerId
    },
    category: ACCOUNT_ACTIONS,
    isAccountScoped: true,
    featureFlag: 'isAnnualIncomeNotificationEnabled',
    isEligible: (ctx) => ctx.isRevolving && ctx.isAccountOpen,
    analyticsReason: 'update income'
  }
]

export default searchItems
